import * as Filter from '../../../util/filters';
import type * as FilterTypes from '../../../util/filterTypes';
import {
  type ConnectorOp,
  EMPTY_STORED_FILTER_V2,
  type FilterV2Item,
  type FilterV2Root,
  getFiltersForQuery,
} from '../../../util/filterV2';
import * as Run from '../../../util/runs';
import type * as RunTypes from '../../../util/runTypes';
import type {AgentActionArgs} from '../agentActionHandlers';

const SECTIONS: RunTypes.RunKeySection[] = [
  'run',
  'tags',
  'config',
  'summary',
  'keys_info',
  'aggregations_min',
  'aggregations_max',
];
const VALUE_OPS = [
  '=',
  '!=',
  '<',
  '>',
  '<=',
  '>=',
  'REGEX',
  'EXISTS',
  'CONTAINS',
  'CONTAINS_WILDCARD',
  'WITHINSECONDS',
];

interface AgentCondition {
  section?: unknown;
  name?: unknown;
  op?: unknown;
  value?: unknown;
  connector?: unknown;
  key?: {section?: unknown; name?: unknown}; // tolerated fallback if a model nests it
}
interface AgentGroup {
  filters?: unknown[];
  connector?: unknown;
}
type AgentItem = AgentCondition | AgentGroup;

function isGroup(item: AgentItem): item is AgentGroup {
  return Array.isArray((item as AgentGroup).filters);
}
function condSection(c: AgentCondition): unknown {
  return c.section ?? c.key?.section;
}
function condName(c: AgentCondition): unknown {
  return c.name ?? c.key?.name;
}
function itemsFrom(args: AgentActionArgs): AgentItem[] {
  return Array.isArray(args.filters) ? (args.filters as AgentItem[]) : [];
}

export function buildFilterRootFromArgs(args: AgentActionArgs): FilterV2Root {
  return {
    ...EMPTY_STORED_FILTER_V2,
    filters: itemsFrom(args).map((it, i) => buildItem(it, i === 0)),
  };
}

function buildItem(raw: AgentItem, isFirst: boolean): FilterV2Item {
  const connector: {connector?: ConnectorOp} = isFirst
    ? {}
    : {connector: normalizeConnector(raw.connector)};
  if (isGroup(raw)) {
    return {
      filters: (raw.filters as AgentItem[]).map((c, i) =>
        buildItem(c, i === 0)
      ),
      ...connector,
    };
  }
  const op = normalizeOp(raw.op);
  return {
    key: {
      section: normalizeSection(condSection(raw)),
      name: String(condName(raw) ?? ''),
    },
    op,
    value: coerceValue(raw.value, op),
    ...connector,
  } as FilterV2Item;
}

function normalizeConnector(c: unknown): ConnectorOp {
  return String(c).toUpperCase() === 'OR' ? 'OR' : 'AND';
}
function normalizeSection(s: unknown): RunTypes.RunKeySection {
  const v = String(s);
  return (SECTIONS as string[]).includes(v)
    ? (v as RunTypes.RunKeySection)
    : 'config';
}
function normalizeOp(op: unknown): FilterTypes.IndividualOp {
  const u = String(op).toUpperCase();
  if (Filter.isMultiOp(u as FilterTypes.IndividualOp)) {
    return u as FilterTypes.MultiValueOp;
  }
  if (VALUE_OPS.includes(u)) {
    return u as FilterTypes.ValueOp;
  }
  return '=';
}
function coerceValue(
  raw: unknown,
  op: FilterTypes.IndividualOp
): RunTypes.Value {
  if (Filter.isMultiOp(op)) {
    const parts = Array.isArray(raw)
      ? raw
      : String(raw)
          .split(',')
          .map(p => p.trim());
    // parseValue widens to Value; the IN/NIN value is the array of parsed scalars.
    return parts
      .map(p => Run.parseValue(p))
      .filter(v => v !== '') as RunTypes.Value;
  }
  return Run.parseValue(raw);
}

// Fallback title (shown after the "Run Filter" name) when the agent gives no
// `label`. Builds the filter tree and reuses the shared summary string (the same
// one the rest of the UI shows), so the wording stays consistent.
export function summarizeRunFilter(args: AgentActionArgs): string {
  const tree = getFiltersForQuery(buildFilterRootFromArgs(args));
  return Filter.countIndividual(tree) === 0
    ? 'Clear'
    : Filter.summaryString(tree);
}
